import React, { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { deleteUser } from 'firebase/auth';
import { auth } from '../../firebase';
import { useSession } from '../context/Session';
import { useLanguage } from '../../contexts/LanguageContext';
import { useT } from '../i18n';
import { Icon, Modal, Switch, useConfirm, useToast, appStoreLink } from '../components/ui';
import { BusyButton, WaterGoalModal } from '../components/nutrition';
import { archiveAndDeleteUserDoc, formatLiters, sendFeedback, updateReminderMinute, updateUser } from '../lib/services';
import { minuteToHHMM } from '../lib/dates';
import { redeemPromoCode } from '../lib/cloud';
import { everydayUnitsEnabled, setEverydayUnitsEnabled } from '../lib/unitPreference';
import { allStarterTodosDone } from './Home';

function Section({ title, children }) {
  const t = useT();
  return (
    <section className="gm-card gm-card--pad-lg">
      <div className="gm-upper-sm gm-muted" style={{ marginBottom: 4 }}>{t.upper(title)}</div>
      {children}
    </section>
  );
}

function Setting({ title, desc, children }) {
  return (
    <div className="gm-setting">
      <div className="gm-setting__text">
        <div className="gm-setting__title">{title}</div>
        {desc && <div className="gm-setting__desc">{desc}</div>}
      </div>
      {children}
    </div>
  );
}

function TimeInput({ minute, onChange, disabled }) {
  const options = [];
  for (let m = 0; m < 1440; m += 15) options.push(m);
  return (
    <select className="gm-select" style={{ width: 'auto', minWidth: 96 }} value={minute} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))}>
      {options.map((m) => <option key={m} value={m}>{minuteToHHMM(m)}</option>)}
    </select>
  );
}

function FeedbackForm({ user }) {
  const t = useT();
  const toast = useToast();
  const [type, setType] = useState('general');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await sendFeedback(user.id, type, msg);
      setMsg('');
      toast.success(t('feedbackSuccess'));
    } catch (e) {
      toast.error(t('feedbackError'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="gm-stack-sm" style={{ marginTop: 8 }}>
      <p className="gm-muted gm-small">{t('feedbackSectionDescription')}</p>
      <div className="gm-chips">
        {[['bug', 'feedbackTypeBug'], ['feature', 'feedbackTypeFeature'], ['ux', 'feedbackTypeUx'], ['general', 'feedbackTypeGeneral']].map(([v, k]) => (
          <button key={v} type="button" className={`gm-chip${type === v ? ' selected' : ''}`} onClick={() => setType(v)}>{t(k)}</button>
        ))}
      </div>
      <textarea className="gm-textarea" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={t('feedbackMessagePlaceholder')} maxLength={2000} />
      <div className="gm-row" style={{ justifyContent: 'flex-end' }}>
        <BusyButton busy={busy} disabled={!msg.trim()} onClick={send}><Icon name="send" size={18} /> {t('feedbackSend')}</BusyButton>
      </div>
    </div>
  );
}

const PROMO_MESSAGES = {
  invalid: 'promoCodeInvalid',
  expired: 'promoCodeExpired',
  alreadyUsed: 'promoAlreadyUsed',
  alreadyPremium: 'promoAlreadyPremium',
  usedUp: 'promoUsedUp',
};

function PromoCodeForm() {
  const t = useT();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    const value = code.trim().toUpperCase();
    if (!value || busy) return;
    setBusy(true);
    try {
      const res = await redeemPromoCode(value);
      if (res?.status === 'premiumGranted') {
        setCode('');
        toast.success(t('promoCodePremiumApplied'), { duration: 5000 });
      } else {
        toast.error(t(PROMO_MESSAGES[res?.status] || 'promoError'));
      }
    } catch (e) {
      toast.error(t('promoError'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="gm-stack-sm" style={{ marginTop: 14 }}>
      <div className="gm-setting__title">{t('promoRedeemTitle')}</div>
      <p className="gm-small gm-muted">{t('promoRedeemDesc')}</p>
      <form className="gm-row" onSubmit={(e) => { e.preventDefault(); apply(); }}>
        <input
          className="gm-input"
          style={{ flex: 1, textTransform: 'uppercase' }}
          value={code}
          maxLength={32}
          autoComplete="off"
          spellCheck={false}
          aria-label={t('promoCode')}
          placeholder={t('promoCodePlaceholder')}
          onChange={(e) => setCode(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''))}
        />
        <BusyButton busy={busy} disabled={!code.trim()} className="gm-btn" onClick={apply}>{t('promoApply')}</BusyButton>
      </form>
    </div>
  );
}

function DeleteAccount() {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async () => {
    setBusy(true);
    setError('');
    try {
      const current = auth.currentUser;
      if (!current) throw new Error('no-session');
      await archiveAndDeleteUserDoc(user.id);
      await deleteUser(current);
      navigate('/');
    } catch (e) {
      if (e?.code === 'auth/requires-recent-login') setError(t('deleteAccountRequiresRecentLogin'));
      else if (e?.message === 'no-session') setError(t('deleteAccountErrorNoSession'));
      else setError(t('anErrorOccurred'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" className="gm-btn gm-btn--outline" style={{ color: 'var(--gm-error)', borderColor: 'var(--gm-error)' }} onClick={() => setOpen(true)}>
        <Icon name="delete_forever" /> {t('deleteAccountTitle')}
      </button>
      <Modal
        open={open}
        onClose={busy ? undefined : () => setOpen(false)}
        title={t('deleteAccountTitle')}
        footer={(
          <>
            <button type="button" className="gm-btn gm-btn--ghost" onClick={() => setOpen(false)} disabled={busy}>{t('cancel')}</button>
            <BusyButton busy={busy} className="gm-btn gm-btn--danger" onClick={run}>{busy ? t('deleteAccountDeleting') : t('deleteAccountConfirmYes')}</BusyButton>
          </>
        )}
      >
        <p className="gm-strong">{t('deleteAccountIrreversibleWarning')}</p>
        <p className="gm-muted" style={{ marginTop: 8 }}>{t('deleteAccountConfirmHint')}</p>
        {error && <p className="gm-error-text" style={{ marginTop: 12 }}>{error}</p>}
      </Modal>
    </>
  );
}

export default function Settings() {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { openPremium } = useOutletContext() || {};
  const { user, theme, setTheme, backgrounds, setBackgrounds, signOut } = useSession();
  const { language, setLanguage } = useLanguage();
  const [waterOpen, setWaterOpen] = useState(false);
  const [everydayUnits, setEverydayUnits] = useState(everydayUnitsEnabled);

  const set = (field) => (v) => updateUser(user.id, { [field]: v }).catch(() => toast.error(t('anErrorOccurred')));
  const setMinute = (kind) => (m) => updateReminderMinute(user.id, kind, m).catch(() => toast.error(t('anErrorOccurred')));

  const planLabel = user.subscription === 'super' ? t('superPlan') : user.subscription === 'limitless' ? t('limitlessPlan') : user.isPremium ? t('premiumPlan') : t('freePlan');

  const logout = async () => {
    const ok = await confirm({ title: t('signOut'), message: t('signOutConfirm'), confirmLabel: t('signOut') });
    if (ok) {
      await signOut();
      navigate('/');
    }
  };

  return (
    <div className="gm-content gm-content--narrow gm-enter">
      <div className="gm-page-head"><h1 className="gm-page-title">{t.upper(t('settings'))}</h1></div>
      <div className="gm-stack">
        <Section title={t('subscription')}>
          <Setting
            title={t('subscriptionPlan')}
            desc={user.isPremium && user.subscriptionSource === 'rc_promo' && user.subscriptionExpiresAt
              ? t('premiumUntil', new Date(user.subscriptionExpiresAt).toLocaleDateString(t.locale, { day: 'numeric', month: 'long', year: 'numeric' }))
              : user.isPremium ? t('subscriptionPremiumDescription') : t('subscriptionFreeDescription')}
          >
            <span className="gm-pill" style={user.isPremium ? { color: 'var(--gm-premium-b)' } : undefined}>{planLabel}</span>
          </Setting>
          {user.isTrainer && (
            <p className="gm-small gm-muted" style={{ marginBottom: 10 }}>{user.isPremium ? t('subscriptionPremiumTrainerLimits') : t('subscriptionFreeTrainerLimits')}</p>
          )}
          <div className="gm-row gm-wrap" style={{ paddingTop: 4 }}>
            {!user.isPremium && (
              <button type="button" className="gm-btn gm-btn--premium" onClick={openPremium}><Icon name="workspace_premium" /> {t('upgradeToPremium')}</button>
            )}
            {user.isPremium && user.subscriptionSource === 'store' && (
              <a className="gm-btn gm-btn--outline" href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noreferrer">{t('cancelSubscription')}</a>
            )}
          </div>
          <p className="gm-tiny gm-muted" style={{ marginTop: 10 }}>{t('planManagedInApp')}</p>
          {user.subscription !== 'limitless' && user.subscription !== 'super' && <PromoCodeForm />}
        </Section>

        <Section title={t('language')}>
          <div className="gm-chips" style={{ paddingTop: 10 }}>
            <button type="button" className={`gm-chip${language === 'tr' ? ' selected' : ''}`} onClick={() => setLanguage('tr')}>{t('turkish')}</button>
            <button type="button" className={`gm-chip${language !== 'tr' ? ' selected' : ''}`} onClick={() => setLanguage('en')}>{t('english')}</button>
          </div>
        </Section>

        <Section title={t('appearance')}>
          <div className="gm-chips" style={{ padding: '10px 0 6px' }}>
            {[['light', 'lightMode', 'light_mode'], ['dark', 'darkMode', 'dark_mode'], ['system', 'systemTheme', 'contrast']].map(([v, k, icon]) => (
              <button key={v} type="button" className={`gm-chip${theme === v ? ' selected' : ''}`} onClick={() => setTheme(v)}><Icon name={icon} /> {t(k)}</button>
            ))}
          </div>
          <Setting title={t('settingsBackgroundImagesTitle')} desc={t('settingsBackgroundImagesSubtitle')}>
            <Switch checked={backgrounds} onChange={setBackgrounds} />
          </Setting>
        </Section>

        <Section title={t('settingsStarterTodosSectionTitle')}>
          <Setting title={t('settingsStarterTodosToggleTitle')} desc={t('settingsStarterTodosToggleSubtitle')}>
            <Switch checked={user.starterTodosEnabled} onChange={set('starterTodosEnabled')} />
          </Setting>
          {user.starterTodosEnabled && allStarterTodosDone(user) && (
            <div className="gm-banner" style={{ marginTop: 4 }}><Icon name="task_alt" size={18} style={{ color: 'var(--gm-nutrition)' }} />{t('starterTodosAllDone')}</div>
          )}
        </Section>

        {!user.isTrainer && (
          <Section title={t('settingsProgramDisplaySectionTitle')}>
            <Setting title={t('settingsProgramDurationPrimaryTitle')} desc={t('settingsProgramDurationPrimarySubtitle')}>
              <Switch checked={user.programDurationInsteadOfSetsReps} onChange={set('programDurationInsteadOfSetsReps')} />
            </Setting>
            <Setting title={t('settingsSyncExerciseAcrossDaysTitle')} desc={t('settingsSyncExerciseAcrossDaysSubtitle')}>
              <Switch checked={user.syncExerciseAcrossDays} onChange={set('syncExerciseAcrossDays')} />
            </Setting>
          </Section>
        )}

        <Section title={t('settingsNutritionSectionTitle')}>
          <Setting title={t('settingsEverydayUnitsTitle')} desc={t('settingsEverydayUnitsSubtitle')}>
            <Switch
              checked={everydayUnits}
              onChange={(on) => {
                setEverydayUnitsEnabled(on);
                setEverydayUnits(on);
              }}
            />
          </Setting>
        </Section>

        {!user.isTrainer && (
          <Section title={t('waterSettingsSectionTitle')}>
            <Setting title={t('waterGoalSettingLabel')} desc={t('waterGoalHint')}>
              <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={() => setWaterOpen(true)}>
                <Icon name="water_drop" size={18} style={{ color: 'var(--gm-water)' }} /> {formatLiters(user.waterGoalMl, t.lang)}
              </button>
            </Setting>
          </Section>
        )}

        <Section title={t('notifications')}>
          <p className="gm-tiny gm-muted" style={{ marginTop: 4 }}>{t('webOnlyMobileFeature')}</p>
          <Setting title={t('notificationsNutritionReminderLabel')} desc={t('notificationsNutritionReminderDescription')}>
            <TimeInput minute={user.nutritionReminderMinute} onChange={setMinute('nutrition')} disabled={!user.notificationsNutritionEnabled} />
            <Switch checked={user.notificationsNutritionEnabled} onChange={set('notificationsNutritionEnabled')} />
          </Setting>
          <Setting title={t('notificationsProgramReminderLabel')} desc={t('notificationsProgramReminderDescription')}>
            <TimeInput minute={user.programReminderMinute} onChange={setMinute('program')} disabled={!user.notificationsProgramEnabled} />
            <Switch checked={user.notificationsProgramEnabled} onChange={set('notificationsProgramEnabled')} />
          </Setting>
          <Setting title={t('notificationsWaterReminderLabel')} desc={t('notificationsWaterReminderDescription')}>
            <TimeInput minute={user.waterReminderMinute} onChange={setMinute('water')} disabled={!user.notificationsWaterEnabled} />
            <Switch checked={user.notificationsWaterEnabled} onChange={set('notificationsWaterEnabled')} />
          </Setting>
          <Setting title={t('notificationsWeeklySummaryReminderLabel')} desc={t('notificationsWeeklySummaryReminderDescription')}>
            <Switch checked={user.notificationsWeeklySummaryEnabled} onChange={set('notificationsWeeklySummaryEnabled')} />
          </Setting>
        </Section>

        <Section title={t('feedbackSectionTitle')}>
          <FeedbackForm user={user} />
        </Section>

        <Section title={t('userInformation')}>
          <div className="gm-row gm-wrap" style={{ paddingTop: 10 }}>
            <button type="button" className="gm-btn gm-btn--soft" onClick={logout}><Icon name="logout" /> {t('signOut')}</button>
            <DeleteAccount />
          </div>
          <div className="gm-row gm-wrap gm-small" style={{ marginTop: 14, gap: 16 }}>
            <a className="gm-link" href={`/privacy-policy/${t.lang}`} target="_blank" rel="noreferrer">{t('privacyPolicy')}</a>
            <a className="gm-link" href={`/terms-of-service/${t.lang}`} target="_blank" rel="noreferrer">{t('termsOfService')}</a>
            <a className="gm-link" href={appStoreLink()} target="_blank" rel="noreferrer">{t('getTheApp')}</a>
          </div>
        </Section>
      </div>
      {waterOpen && <WaterGoalModal uid={user.id} goal={user.waterGoalMl} onClose={() => setWaterOpen(false)} />}
    </div>
  );
}

